import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { SeoHead, organizationLd, webSiteLd, datasetLd } from '../seo';
import cities from '../data/cities.json';

interface CityLeaderboard {
  city: string;
  avg_monthly: number | null;
  count: number;
}

interface OverallStats {
  parking_spots: number;
  seeker_pins: number;
  rent_pins: number;
  matches: number;
}

export default function Stats() {
  const [overall, setOverall] = useState<OverallStats | null>(null);
  const [board, setBoard] = useState<CityLeaderboard[]>([]);
  const [updatedAt, setUpdatedAt] = useState<string>('');

  useEffect(() => {
    api.getOverallStats().then(setOverall).catch(() => undefined);
    fetch('/api/stats/leaderboard?sort=expensive&min_count=1&limit=50')
      .then((r) => r.json())
      .then((j) => {
        if (Array.isArray(j?.data?.results)) setBoard(j.data.results);
      })
      .catch(() => undefined);
    setUpdatedAt(new Date().toISOString());
  }, []);

  return (
    <>
      <SeoHead
        title="India Parking Stats — Live Crowdsourced Data | Parksy"
        description="Live pan-India parking stats: total spots, seekers, price pins, and a city leaderboard of average monthly parking rates."
        canonical="https://parksy.rent/stats"
        ogImage="https://parksy.rent/og/stats.png"
        schemas={[
          organizationLd(),
          webSiteLd(),
          datasetLd({
            name: 'India parking stats (live)',
            description: 'Live pan-India parking stats: total listings, seekers, price pins, and average monthly price by city.',
            url: 'https://parksy.rent/stats',
            citySlug: 'india',
            updatedAt: updatedAt,
          }),
        ]}
      />
      <div className="max-w-5xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">India parking stats</h1>
        <p className="text-gray-600 mb-8">
          Live, crowdsourced data from {cities.length} Indian cities. Updated continuously.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {[
            { label: 'Spots listed', value: overall?.parking_spots, color: 'parksy' },
            { label: 'Active seekers', value: overall?.seeker_pins, color: 'blue' },
            { label: 'Price pins', value: overall?.rent_pins, color: 'purple' },
            { label: 'Matches made', value: overall?.matches, color: 'amber' },
          ].map((s) => (
            <div key={s.label} className="bg-white border rounded-xl p-5 text-center">
              <div className={`text-3xl font-bold text-${s.color}-600`}>
                {s.value ?? '—'}
              </div>
              <div className="text-xs uppercase tracking-wide text-gray-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        <h2 className="text-2xl font-semibold text-gray-900 mb-4">City leaderboard — average monthly parking</h2>
        <div className="bg-white border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-2">City</th>
                <th className="text-right px-4 py-2">Listings</th>
                <th className="text-right px-4 py-2">Avg ₹/month</th>
              </tr>
            </thead>
            <tbody>
              {board.length === 0 && (
                <tr>
                  <td colSpan={3} className="text-center text-gray-500 py-6">
                    Leaderboard populates as listings grow.
                  </td>
                </tr>
              )}
              {board.map((row) => (
                <tr key={row.city} className="border-t">
                  <td className="px-4 py-2 font-medium text-gray-900">
                    <Link to={`/${row.city.toLowerCase()}`} className="hover:text-parksy-600">
                      {row.city}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-right text-gray-600">{row.count}</td>
                  <td className="px-4 py-2 text-right text-gray-600">
                    {row.avg_monthly ? `₹${Math.round(row.avg_monthly).toLocaleString('en-IN')}` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="mt-6 text-xs text-gray-500">
          Data is crowdsourced and may contain outliers. We apply automatic filtering for
          reports and statistical anomalies.
        </p>
      </div>
    </>
  );
}
